import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Settings as SettingsIcon, Sun, Moon, MoonStar, Globe, Bell,
  Pencil, Dumbbell, Layers, ChevronRight, Check, Palette, Bug, Sparkles, Clock,
  Type, Volume2, Loader2, RefreshCcw, PartyPopper,
} from 'lucide-react'
import { fireWinCelebration } from '../../lib/celebration'
import { Card, CardContent } from '../../components/ui/card'
import ReminderSettings from '../../components/ReminderSettings'
import BriefingSettings from '../../components/BriefingSettings'
import PullToRefresh from '../../components/PullToRefresh'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { AZURE_VOICES, DEFAULT_VOICE, readVoicePref, writeVoicePref } from '../../lib/azureVoices'
import { useAiDebugEnabled } from '../../hooks/useAiDebugEnabled'
import { useDebugLogEnabled } from '../../hooks/useDebugLogEnabled'
import { useAiThinkingEnabled } from '../../hooks/useAiThinkingEnabled'
import { useAppFont } from '../../hooks/useAppFont'
import { useShowAiProvider } from '../../hooks/useShowAiProvider'
import { isOwner } from '../../lib/owner'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Reusable row inside a settings section. Keeps the visual consistent
// across "tap-to-action" rows (theme/language/edit-profile/etc.) without
// each row reinventing its own padding/hover.
function Row({ icon: Icon, accent, label, value, onClick, isLast }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-start hover:bg-accent transition-colors',
        !isLast && 'border-b border-border/60'
      )}
    >
      <Icon className={cn('w-4 h-4 flex-shrink-0', accent)} />
      <span className="flex-1 min-w-0 text-sm text-foreground" dir="auto">{label}</span>
      {value && (
        <span className="text-xs text-muted-foreground" dir="auto">{value}</span>
      )}
      <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100" />
    </button>
  )
}

// Owner-only switch row used inside the Developer section. Mirrors
// the Row component shape but ends in a toggle instead of a chevron.
function DevActionRow({ icon: Icon, label, description, onClick, isLast }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full p-4 flex items-center gap-3 text-start hover:bg-accent transition-colors',
        !isLast && 'border-b border-border/60',
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0 text-app-primary-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground" dir="auto">{label}</p>
        {description && (
          <p className="text-[11px] text-muted-foreground" dir="auto">{description}</p>
        )}
      </div>
    </button>
  )
}

function DevToggleRow({ icon: Icon, label, description, checked, onChange, isLast }) {
  return (
    <div
      className={cn(
        'p-4 flex items-center gap-3',
        !isLast && 'border-b border-border/60',
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0 text-app-primary-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground" dir="auto">{label}</p>
        {description && (
          <p className="text-[11px] text-muted-foreground" dir="auto">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-app-primary-500' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  )
}

function SectionHeader({ label }) {
  return (
    <p
      className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mt-4 mb-2 px-1"
      dir="auto"
    >
      {label}
    </p>
  )
}

// Voice picker per language. Renders a chip row per language with
// a Preview-on-tap behaviour — tapping a chip writes the preference
// and immediately plays a short sample so the owner hears the voice
// before committing. The sample text matches the language.
function VoicePicker({ lang, label, sampleText, t }) {
  const [, force] = React.useState(0)
  const tts = useTextToSpeech()
  const current = readVoicePref(lang) || DEFAULT_VOICE[lang]
  const voices = AZURE_VOICES[lang] || []
  const handlePick = (id) => {
    writeVoicePref(lang, id)
    force((n) => n + 1) // re-render to reflect the new selection
    // Fire-and-forget sample play. The next tap of the speaker on
    // any vocab card / digest / article will use the new voice from
    // here on — no extra wiring needed.
    tts.play(sampleText, lang)
  }
  return (
    <div className="px-4 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <Volume2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm text-foreground" dir="auto">{label}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {voices.map((v) => {
          const active = current === v.id
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => handlePick(v.id)}
              className={cn(
                'shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors whitespace-nowrap',
                active
                  ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                  : 'border-input text-muted-foreground hover:bg-accent',
              )}
              dir="auto"
            >
              {active && <Check className="w-3 h-3 inline me-1" />}
              {v.label}
            </button>
          )
        })}
      </div>
      {tts.playing && (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground" dir="auto">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('settings.voice.previewing')}</span>
        </div>
      )}
    </div>
  )
}

export function SettingsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const goBack = () => navigate('/')
  const { themeMode, setThemeMode, autoTheme, setAutoTheme, palette, setPalette, paletteKeys, palettes } = useTheme()
  const { fontId, changeFont, fonts: appFonts } = useAppFont()
  const { user } = useAuth()
  const { resetTour } = useProfile()
  const [aiDebug, setAiDebug] = useAiDebugEnabled()
  const [aiThinking, setAiThinking] = useAiThinkingEnabled()
  const { enabled: showAiProvider, setShowProvider } = useShowAiProvider()
  const [debugLog, setDebugLog] = useDebugLogEnabled()
  const ownerView = isOwner(user)

  const THEME_OPTIONS = [
    { key: 'light', icon: Sun, label: t('settings.appearance.themeLight') },
    { key: 'dark', icon: Moon, label: t('settings.appearance.themeDark') },
    { key: 'black', icon: MoonStar, label: t('settings.appearance.themeBlack') },
  ]

  const isArabic = i18n.language === 'ar'
  const LANGUAGES = [
    { code: 'ar', label: 'العربية' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
  ]

  // Edit profile is still a state-driven takeover inside PhoenixTracker
  // (the onboarding wizard reused in edit mode), so route back to
  // /phoenix and fire the event for the listener mounted there.
  const handleEditProfile = () => {
    navigate('/')
    window.dispatchEvent(new Event('phoenix:edit-profile'))
  }

  // Resets tour_seen_at so the spotlight tour re-fires on the next
  // home-page mount. We navigate to / so the user lands on the
  // anchored Today layout instead of staying on Settings (the tour
  // steps target Today's sections).
  const handleReplayTour = async () => {
    await resetTour()
    navigate('/')
  }

  const handleWorkouts = () => {
    navigate('/workout')
  }

  const handlePhases = () => {
    navigate('/phases')
  }

  // Settings has no remote data to refetch, but the gesture should
  // still feel responsive on every page — performRefresh resolves
  // after the 1s spinner floor + a service-worker update poke.
  const handleRefresh = useCallback(() => performRefresh([]), [])

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-2 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
            <SettingsIcon className="w-5 h-5 text-app-primary-500" />
            {t('settings.title')}
          </h1>
        </div>
      </div>

      {/* Preferences */}
      <SectionHeader label={t('settings.sections.preferences')} />
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm text-foreground" dir="auto">{t('settings.sections.preferences')}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => i18n.changeLanguage(lang.code)}
                className={cn(
                  'h-10 rounded-lg text-sm font-semibold transition-colors',
                  i18n.language === lang.code
                    ? 'bg-app-primary-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
                dir="auto"
              >
                {lang.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Appearance: 3-way theme picker + primary colour palette.
          Theme buttons set themeMode directly; 'black' is the OLED
          variant that flips an extra `oled` class on <html>. */}
      <SectionHeader label={t('settings.sections.appearance')} />
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Auto-by-time toggle. When on, the page paints light during
              the 06:00–18:00 window and dark/black (per the user's
              saved night choice) outside it. The picker below stays
              live so the user can still pick which dark variant to
              use at night. */}
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 flex-shrink-0 text-app-primary-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground" dir="auto">
                {t('settings.appearance.autoTheme')}
              </p>
              <p className="text-[11px] text-muted-foreground" dir="auto">
                {t('settings.appearance.autoThemeHint')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={autoTheme}
              onClick={() => setAutoTheme(!autoTheme)}
              className={cn(
                'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors mt-0.5',
                autoTheme ? 'bg-app-primary-500' : 'bg-muted',
              )}
            >
              <span
                className={cn(
                  'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                  autoTheme ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>

          <div>
            <p className="text-sm text-foreground mb-2" dir="auto">
              {t('settings.appearance.theme')}
            </p>
            {autoTheme && (
              <p className="text-[11px] text-muted-foreground mb-2" dir="auto">
                {t('settings.appearance.autoThemeNightHint')}
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {THEME_OPTIONS.map(({ key, icon: Icon, label }) => {
                const active = themeMode === key
                // When auto is on the Light slot is purely informational
                // — daytime always paints light regardless of the saved
                // mode — so disable it to make the constraint visible.
                const disabled = autoTheme && key === 'light'
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setThemeMode(key)}
                    aria-pressed={active}
                    disabled={disabled}
                    className={cn(
                      'flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border transition-colors',
                      active
                        ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                        : 'border-input text-muted-foreground hover:bg-accent',
                      disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[11px] font-medium" dir="auto">
                      {label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-app-primary-500" />
            <p className="text-sm text-foreground" dir="auto">
              {t('settings.appearance.primaryColor')}
            </p>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {paletteKeys.map((key) => {
              const p = palettes[key]
              const isActive = key === palette
              const label = i18n.language === 'ar' ? p.label_ar : p.label_en
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPalette(key)}
                  aria-label={label}
                  aria-pressed={isActive}
                  className={cn(
                    'flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-colors',
                    isActive ? 'border-foreground/40 bg-accent' : 'border-input hover:bg-accent'
                  )}
                >
                  <span
                    className="w-9 h-9 rounded-full flex items-center justify-center shadow-sm"
                    style={{ background: `hsl(${p.shades[500]})` }}
                  >
                    {isActive && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                  </span>
                  <span className="text-[11px] text-muted-foreground" dir="auto">
                    {label}
                  </span>
                </button>
              )
            })}
          </div>
          </div>

          {/* Font picker. Each card renders its own preview text in
              the font itself so the user can compare Arabic +
              English shapes before picking. The hook lazy-loads the
              chosen Google Font into <head> on first selection. */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Type className="w-4 h-4 text-app-primary-500" />
              <p className="text-sm text-foreground" dir="auto">
                {t('settings.appearance.font')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {appFonts.map((f) => {
                const isActive = f.id === fontId
                const displayLabel = i18n.language === 'ar' ? f.labelAr : f.labelEn
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => changeFont(f.id)}
                    aria-pressed={isActive}
                    className={cn(
                      'flex flex-col items-start gap-1 p-3 rounded-lg border transition-colors text-start',
                      isActive
                        ? 'border-app-primary-500 bg-app-primary-500/5'
                        : 'border-input hover:bg-accent',
                    )}
                    style={{ fontFamily: f.stack || undefined }}
                  >
                    <span className="flex items-center gap-1.5 w-full">
                      <span className="text-xs font-semibold text-foreground flex-1 truncate" dir="auto">
                        {displayLabel}
                      </span>
                      {isActive && (
                        <Check className="w-3.5 h-3.5 text-app-primary-500 flex-shrink-0" strokeWidth={3} />
                      )}
                    </span>
                    <span className="text-sm text-foreground/80 leading-tight" dir="rtl">
                      أهلا بك
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-tight" dir="ltr">
                      The quick brown fox
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TTS voice picker. Backed by Azure Neural voices via the
          tts-azure edge function; preference per-language so EN and
          AR can each have their own pick. Tap to preview + save. */}
      <SectionHeader label={t('settings.sections.voice')} />
      <Card>
        <CardContent className="p-0">
          <VoicePicker
            lang="en"
            label={t('settings.voice.englishLabel')}
            sampleText={t('settings.voice.englishSample')}
            t={t}
          />
          <div className="border-t border-border/60" />
          <VoicePicker
            lang="ar"
            label={t('settings.voice.arabicLabel')}
            sampleText={t('settings.voice.arabicSample')}
            t={t}
          />
        </CardContent>
      </Card>

      {/* Morning voice briefing — location for the weather + which
          sections to include + news interests. */}
      <SectionHeader label={t('settings.sections.briefing')} />
      <Card>
        <CardContent className="p-0">
          <BriefingSettings />
        </CardContent>
      </Card>

      {/* Notifications — reuses the existing component which already
          covers master toggle + per-kind tiles + (owner-only) debug buttons. */}
      <SectionHeader label={t('settings.sections.notifications')} />
      <Card>
        <CardContent className="p-0">
          <ReminderSettings />
        </CardContent>
      </Card>

      {/* Profile */}
      <SectionHeader label={t('settings.sections.profile')} />
      <Card>
        <CardContent className="p-0">
          <Row
            icon={Pencil}
            accent="text-app-primary-500"
            label={t('profile.editProfile')}
            onClick={handleEditProfile}
          />
          <Row
            icon={Sparkles}
            accent="text-app-primary-500"
            label={t('settings.replayTour')}
            onClick={handleReplayTour}
            isLast
          />
        </CardContent>
      </Card>

      {/* Training */}
      <SectionHeader label={t('settings.sections.training')} />
      <Card>
        <CardContent className="p-0">
          <Row
            icon={Dumbbell}
            accent="text-app-primary-500"
            label={t('profile.workouts')}
            onClick={handleWorkouts}
          />
          <Row
            icon={Layers}
            accent="text-app-primary-500"
            label={t('phoenixTracker.phases.title')}
            onClick={handlePhases}
            isLast
          />
        </CardContent>
      </Card>

      {/* App-wide actions everyone can use. "Check for updates" fires
          the same pwa:update-available event the SW raises on its
          own — handy for partners on slow CDN propagation who don't
          want to wait for the next polled tick to grab the new build. */}
      <SectionHeader label={t('settings.sections.app')} />
      <Card>
        <CardContent className="p-0">
          <DevActionRow
            icon={RefreshCcw}
            label={t('settings.app.checkUpdates.label')}
            description={t('settings.app.checkUpdates.description')}
            onClick={() => {
              window.dispatchEvent(new CustomEvent('pwa:update-available', { detail: {} }))
            }}
            isLast
          />
        </CardContent>
      </Card>

      {/* Owner-only debug switches. Hidden from everyone else via
          isOwner(). The toggle flips localStorage; WorkoutCoach reads
          the same hook and only renders its debug panel when on. */}
      {ownerView && (
        <>
          <SectionHeader label={t('settings.sections.developer')} />
          <Card>
            <CardContent className="p-0">
              <DevToggleRow
                icon={Bug}
                label={t('settings.developer.aiDebug.label')}
                description={t('settings.developer.aiDebug.description')}
                checked={aiDebug}
                onChange={setAiDebug}
              />
              <DevToggleRow
                icon={Sparkles}
                label={t('settings.developer.aiThinking.label')}
                description={t('settings.developer.aiThinking.description')}
                checked={aiThinking}
                onChange={setAiThinking}
              />
              <DevToggleRow
                icon={Bug}
                label={t('settings.developer.showAiProvider.label')}
                description={t('settings.developer.showAiProvider.description')}
                checked={showAiProvider}
                onChange={setShowProvider}
              />
              <DevToggleRow
                icon={Bug}
                label={t('settings.developer.debugLog.label')}
                description={t('settings.developer.debugLog.description')}
                checked={debugLog}
                onChange={setDebugLog}
              />
              <DevActionRow
                icon={RefreshCcw}
                label={t('settings.developer.testUpdate.label')}
                description={t('settings.developer.testUpdate.description')}
                onClick={() => {
                  // Fake an update event so the UpdateToast renders.
                  // The detail object only needs to be truthy; if its
                  // `.waiting` is missing, handleReload's postMessage
                  // is skipped and the 3-second reload fallback fires
                  // — which is exactly the behaviour we want for a
                  // test (the page reloads after the modal proves
                  // its non-dismissible contract holds).
                  window.dispatchEvent(new CustomEvent('pwa:update-available', { detail: {} }))
                }}
              />
              <DevActionRow
                icon={PartyPopper}
                label={t('settings.developer.testCelebration.label')}
                description={t('settings.developer.testCelebration.description')}
                onClick={fireWinCelebration}
                isLast
              />
            </CardContent>
          </Card>
        </>
      )}

    </div>
    </PullToRefresh>
  )
}
